import { useCallback, useEffect, useState } from "react";
import "./permission.scss";
import { Button, Flex, Form, Input, InputNumber, Select, Switch } from "antd";
import { RoleServices } from "../../services/role";
import { useMessageApi } from "../../context/MessageContext";
import { IBodyUserDto, IUserDetailResponse, IUserResult } from "../../interfaces/user";
import { UserServices } from "../../services/user";
import { IRoleResult, IRolesResponse } from "../../interfaces/role";
import { IUserData } from "../../interfaces/auth";
import { useAuth } from "../../context/AuthContext";

interface CreateAndEditUserProps {
  onClose: (okTriggered?: boolean) => void;
  detailUser: IUserResult | undefined;
}

const CreateAndEditUser: React.FC<CreateAndEditUserProps> = ({ onClose, detailUser }) => {
  const messageApi = useMessageApi();
  const [form] = Form.useForm();
  const [roles, setRoles] = useState<IRoleResult[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isLoadingSubmit, setIsLoadingSubmit] = useState<boolean>(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const isEdit = !!detailUser;
  const { user, setUser } = useAuth();

  const checkFormValid = useCallback(() => {
    const hasErrors = form.getFieldsError().some((field) => field.errors.length > 0);
    const current = form.getFieldsValue();

    const isAllFieldsFilled =
      current.email?.trim() !== "" &&
      current.firstName?.trim() !== "" &&
      current.lastName?.trim() !== "" &&
      current.roleId !== undefined &&
      current.roleId !== "";

    const boolEqual = (a: boolean | undefined, b: boolean | undefined) => Boolean(a) === Boolean(b);

    let isChanged = true;
    if (!detailUser) {
      isChanged = isAllFieldsFilled;
    } else {
      isChanged =
        isAllFieldsFilled &&
        !(
          current.email === detailUser.email &&
          current.firstName === detailUser.firstName &&
          current.lastName === detailUser.lastName &&
          Number(current.userLimit) === Number(detailUser.userLimit) &&
          Number(current.roleId) === Number(detailUser.roleId) &&
          boolEqual(current.apEscalation, detailUser.apEscalation) &&
          boolEqual(current.apOwner, detailUser.apOwner) &&
          boolEqual(current.productHead, detailUser.productHead) &&
          boolEqual(current.productOwner, detailUser.productOwner)
        );
    }
    return !hasErrors && isChanged;
  }, [detailUser, form]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setRoles([]);
        setLoading(true);
        const response: IRolesResponse = await RoleServices.getAll();
        if (response.success) {
          setRoles(response.data?.results ?? []);
        }
      } catch (error) {
        console.error("Fetch roles error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (detailUser) {
      form.setFieldsValue({
        email: detailUser.email || "",
        firstName: detailUser.firstName || "",
        lastName: detailUser.lastName || "",
        roleId: detailUser.roleId || undefined,
        apEscalation: detailUser.apEscalation,
        apOwner: detailUser.apOwner,
        productHead: detailUser.productHead,
        productOwner: detailUser.productOwner,
        userLimit: detailUser.userLimit,
      });

      setTimeout(() => {
        setIsFormValid(checkFormValid());
      }, 0);
    } else {
      form.resetFields();
      setIsFormValid(false);
    }
  }, [detailUser, form, checkFormValid]);

  const fetchUser = async () => {
    try {
      const res = (await UserServices.getMe()) as unknown as {
        success: boolean;
        data: IUserData;
      };
      if (res.success) {
        setUser(res.data); // reload new data user info
      }
    } finally {
      setLoading(false);
    }
  };

  const onFinish = async (values: any) => {
    const payload: IBodyUserDto = {
      ...values,
      roleId: Number(values.roleId),
      userLimit: Number(values.userLimit) || 0,
    };
    try {
      setIsLoadingSubmit(true);
      const response: IUserDetailResponse = isEdit
        ? await UserServices.updateUser(detailUser.id, payload)
        : await UserServices.createUser(payload);
      const { success, message } = response;
      if (success) {
        onClose(true);
        messageApi.success(message);
        if (isEdit && Number(detailUser.id) === Number(user?.id)) {
          fetchUser();
        }
      }
    } catch (error: any) {
      if (error && error.details) {
        Object.entries(error.details).forEach(([field, messages]) => {
          form.setFields([
            {
              name: field,
              errors: messages as string[],
            },
          ]);
        });
      } else {
        messageApi.error(error?.message);
      }
    } finally {
      setIsFormValid(checkFormValid());
      setIsLoadingSubmit(false);
    }
  };

  const onValuesChange = () => {
    setTimeout(() => {
      setIsFormValid(checkFormValid());
    }, 0);
  };

  const isRoleSelectDisabled = (
    loading: boolean,
    roles: IRoleResult[],
    selecteduserId: number | undefined,
    currentUserId: string | undefined
  ): boolean => {
    return (loading && roles.length === 0) || String(selecteduserId) === currentUserId;
  };

  return (
    <Form
      form={form}
      layout="horizontal"
      onFinish={onFinish}
      labelCol={{ span: 6 }}
      wrapperCol={{ span: 18 }}
      onValuesChange={onValuesChange}
    >
      <Form.Item
        label="Email"
        name="email"
        rules={[
          { required: true, message: "Complete this required field." },
          { type: "email", message: "Email is not valid!" },
        ]}
      >
        <Input
          placeholder="Enter Email"
          disabled={!!detailUser?.id}
          maxLength={200}
          onBlur={() => {
            form.validateFields(["email"]).catch(() => {});
          }}
        />
      </Form.Item>
      <Form.Item
        label="First Name"
        name="firstName"
        rules={[{ required: true, message: "Complete this required field." }]}
      >
        <Input placeholder="Enter First Name" maxLength={200} />
      </Form.Item>
      <Form.Item
        label="Last Name"
        name="lastName"
        rules={[{ required: true, message: "Complete this required field." }]}
      >
        <Input placeholder="Enter Last Name" maxLength={200} />
      </Form.Item>

      <Form.Item label="Role" name="roleId" rules={[{ required: true, message: "Complete this required field." }]}>
        <Select
          placeholder={loading ? "Loading roles..." : "Select Role"}
          disabled={isRoleSelectDisabled(loading, roles, detailUser?.id, user?.id)}
        >
          {!loading && (
            <Select.Option value="" disabled>
              Select one
            </Select.Option>
          )}
          {detailUser && !roles.find((r) => Number(r.id) === Number(detailUser.roleId)) && (
            <Select.Option key={detailUser.roleId} value={detailUser.roleId}>
              {detailUser.roleName}
            </Select.Option>
          )}
          {roles.map((role) => (
            <Select.Option key={role.id} value={role.id}>
              {role.name}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        label="User Limit"
        name="userLimit"
        rules={[{ required: false, message: "Complete this required field." }]}
      >
        <InputNumber placeholder="Enter User Limit" style={{ width: "100%" }} addonAfter="GBP" />
      </Form.Item>

      <Form.Item label="Product Head" name="productHead" valuePropName="checked">
        <Switch onChange={() => form.setFields([{ name: "productHead", errors: [] }])} />
      </Form.Item>
      <Form.Item label="Product Owner" name="productOwner" valuePropName="checked">
        <Switch onChange={() => form.setFields([{ name: "productOwner", errors: [] }])} />
      </Form.Item>
      <Form.Item label="AP Owner" name="apOwner" valuePropName="checked">
        <Switch onChange={() => form.setFields([{ name: "apOwner", errors: [] }])} />
      </Form.Item>
      <Form.Item label="AP Escalation" name="apEscalation" valuePropName="checked">
        <Switch onChange={() => form.setFields([{ name: "apEscalation", errors: [] }])} />
      </Form.Item>

      <Flex justify="end" gap={16} style={{ marginTop: 20 }}>
        <Button
          style={{ marginLeft: 8 }}
          onClick={() => onClose()}
          disabled={isLoadingSubmit}
          color="primary"
          variant="outlined"
        >
          Cancel
        </Button>
        <Button type="primary" htmlType="submit" loading={isLoadingSubmit} disabled={!isFormValid}>
          Save
        </Button>
      </Flex>
    </Form>
  );
};

export default CreateAndEditUser;
